// GPS puntual per event (CURRENT_TASK.md §21-22): no fer seguiment continu,
// no bloquejar mai el fitxatge per problemes de GPS. Sempre es resol amb un
// dels estats acordats (OK, NO_DISPONIBLE, PERMIS_DENEGAT, PERMIS_BLOQUEJAT,
// ERROR), mai amb un rebuig.

// Best effort: Permissions API no és fiable a totes les versions de Safari/iOS.
// Si no existeix o falla, retorna null i el code === 1 es classifica com
// PERMIS_DENEGAT (comportament compatible amb l'anterior).
async function chConsultarEstatPermisGeolocalitzacio() {
  try {
    if (
      typeof navigator === "undefined" ||
      !navigator.permissions ||
      typeof navigator.permissions.query !== "function"
    ) {
      return null;
    }
    const resultat = await navigator.permissions.query({ name: "geolocation" });
    if (
      resultat &&
      (resultat.state === "granted" ||
        resultat.state === "prompt" ||
        resultat.state === "denied")
    ) {
      return resultat.state;
    }
  } catch {
    // Ignorar: no bloquejar ni mostrar error.
  }
  return null;
}

function chClassificarPermisDenegat(estatInicial) {
  // Només PERMIS_BLOQUEJAT quan el navegador ja reportava "denied" abans
  // de demanar la geolocalització. Qualsevol altre cas (prompt, granted
  // anòmal, o API desconeguda) → PERMIS_DENEGAT.
  if (estatInicial === "denied") return "PERMIS_BLOQUEJAT";
  return "PERMIS_DENEGAT";
}

export const chObtenirGeolocalitzacio = async () => {
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    return { estatGPS: "NO_DISPONIBLE", latitud: null, longitud: null, precisioMetres: null };
  }

  // La Geolocation API només funciona en "secure contexts" (HTTPS, o
  // localhost). Servint el portal per http://IP_LAN:3000 (proves en
  // xarxa local) NO és un secure context: el navegador denega l'accés
  // directament, sense ni mostrar el diàleg de permís, cridant l'error
  // amb code=1 (PERMISSION_DENIED) com si l'empleat l'hagués rebutjat.
  // Es distingeix explícitament com "NO_DISPONIBLE" (limitació de
  // l'entorn, no una decisió de l'empleat) per no confondre-ho amb una
  // denegació real. El fitxatge continua registrant-se igualment.
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    return { estatGPS: "NO_DISPONIBLE", latitud: null, longitud: null, precisioMetres: null };
  }

  const estatInicial = await chConsultarEstatPermisGeolocalitzacio();

  return new Promise((resolve) => {
    let resolt = false;
    const acabar = (resultat) => {
      if (resolt) return;
      resolt = true;
      resolve(resultat);
    };

    // Objectiu: saber aproximadament on és el treballador (desenes de metres
    // basta; una fixa de fa <= 30 s és acceptable). enableHighAccuracy:false
    // i maximumAge:30000 redueixen timeouts a mòbil (sobretot iPhone) sense
    // demanar GPS satèl·lit "fred" en 5 s. Si la precisió es degrada massa
    // a la pràctica, s'afinaran aquests valors.
    const TIMEOUT_API_MS = 12000;
    const TIMEOUT_PROPI_MS = 13000;

    // Marge propi a més del timeout de l'API, per si el navegador no el respecta.
    const timeoutId = setTimeout(() => {
      acabar({ estatGPS: "ERROR", latitud: null, longitud: null, precisioMetres: null });
    }, TIMEOUT_PROPI_MS);

    navigator.geolocation.getCurrentPosition(
      (posicio) => {
        clearTimeout(timeoutId);
        acabar({
          estatGPS: "OK",
          latitud: posicio.coords.latitude,
          longitud: posicio.coords.longitude,
          precisioMetres: posicio.coords.accuracy,
        });
      },
      (error) => {
        clearTimeout(timeoutId);
        // Codis natius de GeolocationPositionError:
        //   1 = PERMISSION_DENIED   -> PERMIS_DENEGAT o PERMIS_BLOQUEJAT
        //   2 = POSITION_UNAVAILABLE -> el dispositiu no pot obtenir-la
        //       (no és una decisió de l'empleat: es tracta com NO_DISPONIBLE)
        //   3 = TIMEOUT              -> transitori, es pot reintentar
        let estatGPS = "ERROR";
        if (error && error.code === 1) estatGPS = chClassificarPermisDenegat(estatInicial);
        else if (error && error.code === 2) estatGPS = "NO_DISPONIBLE";
        acabar({ estatGPS, latitud: null, longitud: null, precisioMetres: null });
      },
      { enableHighAccuracy: false, timeout: TIMEOUT_API_MS, maximumAge: 30000 }
    );
  });
};
